import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface PageHeaderProps {
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children?: ReactNode;
  /** Compact variant for workspace pages with toolbars (Canvas, Brainstorm, etc.) */
  compact?: boolean;
}

export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  actions,
  children,
  compact = false,
}: Readonly<PageHeaderProps>) {
  return (
    <div data-testid="page-header" className={`shrink-0 border-b border-border-primary ${compact ? "p-4" : "p-6"}`}>
      <div className="flex items-center justify-between">
        <div className={`flex items-center ${compact ? "gap-2" : "gap-3"}`}>
          {Icon && (
            <div
              data-testid="page-header-icon"
              className={`rounded-lg bg-brand-gradient-br flex items-center justify-center ${compact ? "w-7 h-7" : "w-9 h-9"}`}
            >
              <Icon className={`text-white ${compact ? "w-4 h-4" : "w-5 h-5"}`} />
            </div>
          )}
          <div>
            <h1
              data-testid="page-header-title"
              className={`font-semibold text-content-primary ${compact ? "text-base" : "text-2xl"}`}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                data-testid="page-header-subtitle"
                className={`text-content-tertiary ${compact ? "text-xs mt-0.5" : "mt-1"}`}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div data-testid="page-header-actions" className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
